/**
 * Project case studies.
 *
 * `outcome` numbers are the ones worth double checking before you publish.
 * Swap in real figures where you have them, or drop the metric entirely.
 * `github` and `demo` are optional; buttons only render when a link exists.
 */

export const projects = [
  {
    id: 'converge',
    title: 'Converge',
    category: 'Real Estate · AI',
    year: '2024',
    folder: 'converige',
    cover: '/images/converge.png',
    tags: ['Flutter', 'AI', 'Real Estate'],
    summary: 'An AI-assisted property platform that matches buyers to listings and keeps agents in the loop.',
    overview:
      'Converge is a real-estate platform where buyers describe what they want in plain language and get matched to listings, with an agent available in the same thread. I built the mobile app and the services behind the matching and chat experience.',
    problem:
      'Buyers were scrolling through hundreds of near-identical listings and giving up. Agents were losing warm leads simply because replies took hours, by which point the buyer had moved on.',
    solution:
      'A conversational search layer that turns a description into a ranked shortlist, paired with real-time agent chat so a human can step in the moment interest is real. Responses stream in immediately rather than waiting on a full result set.',
    role:
      'Mobile lead. I owned the Flutter app end to end, designed the chat and matching APIs, and worked with the founders on what the matching should actually optimise for.',
    features: [
      'Natural-language property search with ranked matches',
      'Real-time agent chat with typing and read state',
      'Saved searches and instant alerts on new listings',
      'Map-based browsing with clustered results',
      'Secure document sharing between agent and buyer'
    ],
    tech: ['Flutter', 'Node.js', 'TensorFlow', 'Firebase', 'Google Maps API', 'PostgreSQL', 'AWS'],
    outcome: [
      { value: '+35%', label: 'Qualified leads' },
      { value: '<3s', label: 'First response' },
      { value: '10k+', label: 'Monthly enquiries' }
    ],
    lesson:
      'Perceived speed mattered more than match quality. Streaming a good answer instantly beat delivering a better one a few seconds later.',
    demo: 'https://converge.ai/',
    media: {
      images: ['IMG_2780.PNG', 'IMG_3250.PNG', 'IMG_3251.PNG', 'IMG_3252.PNG', 'IMG_3254.PNG']
    }
  },
  {
    id: 'gift-guide',
    title: 'The Gift Guide',
    category: 'E-Commerce',
    year: '2023',
    folder: 'gift_guide',
    cover: '/images/the_gift_guid.png',
    tags: ['React Native', 'E-Commerce', 'Recommendations'],
    summary: 'Gift discovery that starts from the recipient rather than a product category.',
    overview:
      'A shopping app built around a question most catalogues cannot answer: what should I get this particular person? It builds a shortlist from who the gift is for and the occasion, then gets out of the way at checkout.',
    problem:
      'Gift shopping is browsing without a target. People opened the app, scrolled a generic catalogue, and left without buying because nothing felt specific to the person they had in mind.',
    solution:
      'A short guided flow that captures recipient, occasion and budget, feeding a recommendation engine that learns from what people actually save and buy. Checkout was cut down to the fewest possible steps.',
    role:
      'Full-stack engineer. Built the React Native app, the recommendation service and the Stripe checkout flow.',
    features: [
      'Recipient-first guided recommendations',
      'Occasion reminders and saved wishlists',
      'One-tap checkout with saved payment methods',
      'Order and delivery tracking',
      'Curated collections by budget'
    ],
    tech: ['React Native', 'Node.js', 'MongoDB', 'Stripe', 'AWS'],
    outcome: [
      { value: '+40%', label: 'Average order value' },
      { value: '2x', label: 'Faster checkout' },
      { value: '4.7', label: 'Store rating' }
    ],
    lesson:
      'Asking two extra questions up front raised conversion more than any change we made to the catalogue itself.',
    demo: 'https://thegiftguide.com/',
    media: {
      images: [
        'Screenshot_1700351166.png',
        'Screenshot_1700351205.png',
        'Screenshot_1700351487.png',
        'Screenshot_1700351517.png',
        'Screenshot_1700353240.png'
      ]
    }
  },
  {
    id: 'sparkd',
    title: 'SparkdAI',
    category: 'Social · AI',
    year: '2024',
    folder: 'sparkd',
    cover: '/images/sparkd_ai.jpg',
    tags: ['Flutter', 'AI', 'Subscriptions'],
    summary: 'A messenger with AI assistance built in, plus a subscription tier that actually converts.',
    overview:
      'SparkdAI is a messaging app where AI helps you write, not just autocomplete. It shipped on the App Store with in-app subscriptions and a free tier that stays genuinely usable.',
    problem:
      'Messaging apps are a crowded space and AI features were mostly gimmicks bolted on top. The product needed assistance that felt useful in normal conversation, and a paid tier people would not resent.',
    solution:
      'Contextual reply suggestions that read the thread rather than the last message, tone adjustment before sending, and a subscription that unlocks depth instead of removing artificial limits.',
    role:
      'Lead mobile engineer. Built the Flutter app, the real-time messaging layer, and the full in-app purchase and entitlement flow across both stores.',
    features: [
      'Context-aware smart replies',
      'Tone rewriting before you send',
      'Tiered subscriptions with StoreKit and Play Billing',
      'End-to-end encrypted direct messages',
      'Group chats with roles and moderation'
    ],
    tech: ['Flutter', 'Firebase', 'OpenAI', 'WebRTC', 'Node.js', 'AWS'],
    outcome: [
      { value: '4.8/5', label: 'App Store rating' },
      { value: '5k+', label: 'Reviews' },
      { value: 'Live', label: 'On the App Store' }
    ],
    lesson:
      'Subscription plumbing took as long as the AI features. Receipt validation and restore flows deserve real time in the estimate.',
    demo: 'https://apps.apple.com/pk/app/sparkdai/id6504047689',
    media: {
      images: ['IMG_4341.PNG', 'IMG_4850.PNG', 'IMG_4853.PNG', 'IMG_4855.PNG']
    }
  },
  {
    id: 'gluto',
    title: 'Gluto',
    category: 'AI · Automation',
    year: '2025',
    folder: 'gluto',
    cover: '/images/gluto.png',
    tags: ['Python', 'Agents', 'Automation'],
    summary: 'An agent that carries out multi-step work across the tools a team already uses.',
    overview:
      'Gluto takes a plain language instruction and completes it across connected tools, reading context, deciding the next step, and reporting back what it did. Built for the repetitive workflows that sit between apps.',
    problem:
      'Teams were losing hours to work that was too varied to script but too routine to need a person: moving data between systems, chasing status, compiling the same reports every week.',
    solution:
      'An agent loop with tool access and an explicit plan then execute step, so every action is inspectable. Anything irreversible pauses for confirmation rather than guessing.',
    role:
      'Backend and AI engineer. Designed the agent loop, the tool interface, and the execution log that makes runs auditable.',
    features: [
      'Natural-language task instructions',
      'Multi-step planning with visible reasoning',
      'Pluggable tool and API integrations',
      'Human confirmation before destructive actions',
      'Full audit log of every run'
    ],
    tech: ['Python', 'FastAPI', 'OpenAI', 'LangChain', 'PostgreSQL', 'Docker'],
    outcome: [
      { value: 'Hours', label: 'Saved weekly per team' },
      { value: '100%', label: 'Actions logged' }
    ],
    lesson:
      'Agents get trusted through transparency. Showing the plan before running it did more for adoption than improving accuracy.',
    media: {
      images: ['1_d.png', '2_d.png', '3_d.png', '4_d.png', '5_d.png', '6_d.png']
    }
  },
  {
    id: 'veritey',
    title: 'Veritey Healthcare',
    category: 'Healthcare',
    year: '2024',
    folder: 'veritey-health-care-solution',
    cover: '/images/varitey_care.png',
    tags: ['Flutter', 'Healthcare', 'Scheduling'],
    summary: 'Patient records, scheduling and secure messaging in one clinical app.',
    overview:
      'A healthcare management app connecting patients and providers: appointments, records and messaging, built to the handling standards that clinical data demands.',
    problem:
      'Clinic staff were juggling phone calls, paper files and a scheduling tool that did not talk to any of them. Patients had no way to see their own history.',
    solution:
      'A single app for both sides. Patients book, view records and message their provider; staff manage the schedule and prescriptions from the same source of truth.',
    role:
      'Mobile engineer. Built the Flutter app, offline-first record sync, and the encrypted messaging layer.',
    features: [
      'Appointment booking with live availability',
      'Patient records with offline access',
      'Encrypted messaging between patients and providers',
      'Prescription issue and renewal',
      'Role-based access for clinic staff'
    ],
    tech: ['Flutter', 'Firebase', 'Node.js', 'MongoDB', 'AWS'],
    outcome: [
      { value: '−60%', label: 'Scheduling calls' },
      { value: 'Offline', label: 'Record access' }
    ],
    lesson:
      'Offline-first was not optional. Clinic wifi is unreliable and staff will abandon a tool that stalls mid-appointment.',
    media: {
      images: ['IMG_4909.PNG', 'IMG_4910.PNG', 'IMG_4911.PNG', 'IMG_4912.PNG', 'IMG_4913.PNG']
    }
  },
  {
    id: 'armada',
    title: 'Armada',
    category: 'Mobility',
    year: '2023',
    folder: 'armada',
    cover: '/images/armada_car.jpg',
    tags: ['Flutter', 'Marketplace', 'Payments'],
    summary: 'Hourly peer-to-peer car rental with payments and insurance handled in-app.',
    overview:
      'A marketplace for renting cars by the hour rather than the day, covering listing, booking, payment and handover between two strangers.',
    problem:
      'Daily-rate rentals price out short trips, and peer-to-peer sharing stalls on trust: who pays if something goes wrong, and how does the handover work without meeting?',
    solution:
      'Hourly pricing with insurance folded into the booking, held payments released after a clean return, and an in-app handover checklist with timestamped photos from both sides.',
    role:
      'Mobile engineer. Built the Flutter app, booking and availability logic, and Stripe payment flows including holds and refunds.',
    features: [
      'Hourly booking with real-time availability',
      'Integrated insurance at checkout',
      'Held payments with automatic release',
      'Photo-documented handover checklist',
      'In-app messaging and trip tracking'
    ],
    tech: ['Flutter', 'Firebase', 'Stripe', 'Google Maps API', 'Node.js', 'AWS'],
    outcome: [
      { value: 'Hourly', label: 'Booking model' },
      { value: '2-sided', label: 'Marketplace' }
    ],
    lesson:
      'The handover checklist resolved nearly every dispute. A small feature carried most of the trust.',
    media: {
      images: ['Wallet.png', 'Messaging.png', 'Chat.png', 'Car Detailzs.png', 'Payment.png']
    }
  },
  {
    id: 'carryme',
    title: 'CarryMe',
    category: 'Logistics',
    year: '2022',
    folder: 'carryme',
    cover: '/images/carry_me.jpg',
    tags: ['Flutter', 'Logistics', 'Maps'],
    summary: 'Cross-border delivery matching senders with travellers already making the trip.',
    overview:
      'A logistics app connecting people who need something delivered internationally with travellers who have spare luggage capacity, with tracking and payment handled end to end.',
    problem:
      'International courier pricing makes small parcels uneconomical, while thousands of travellers fly the same routes daily with unused capacity and no safe way to monetise it.',
    solution:
      'Route matching between senders and travellers, held payment released on confirmed delivery, and verification on both sides before anything is handed over.',
    role:
      'Mobile engineer. Built the Flutter app, live tracking, and the route-matching interface.',
    features: [
      'Route-based sender and traveller matching',
      'Live parcel tracking',
      'Escrow payments released on delivery',
      'Identity verification for both parties',
      'In-app chat and delivery proof'
    ],
    tech: ['Flutter', 'Firebase', 'Google Maps API', 'Stripe', 'Node.js', 'AWS'],
    outcome: [
      { value: 'Global', label: 'Route coverage' },
      { value: 'Escrow', label: 'Payment safety' }
    ],
    lesson:
      'Verification friction early in the flow cost signups but almost eliminated disputes. Worth the trade.',
    demo: 'https://linkedin.com/company/carrymeaesier',
    media: { images: ['carry_me.jpg', 'carry_me2.jpg'] }
  },
  {
    id: 'healthtok',
    title: 'HealthTok',
    category: 'Health · Telemedicine',
    year: '2021',
    folder: 'healthtok',
    cover: '/images/health_tok.jpg',
    tags: ['Android', 'WebRTC', 'Wearables'],
    summary: 'Video consultations with live vitals streamed from the patient\u2019s smartwatch.',
    overview:
      'A telemedicine app for doctor and patient video consultations, with wearable data pulled in so the doctor sees vitals during the call rather than asking for them.',
    problem:
      'Remote consultations were guesswork. Doctors had to rely on patients self-reporting numbers they often measured incorrectly or not at all.',
    solution:
      'WebRTC video with a live vitals panel fed from connected smartwatches, and readings written straight into the consultation record.',
    role:
      'Android engineer. Built the native app, the WebRTC integration, and the wearable data pipeline.',
    features: [
      'Low-latency WebRTC video consultations',
      'Live smartwatch vitals during calls',
      'Historical health trends per patient',
      'Digital prescriptions',
      'Appointment scheduling and reminders'
    ],
    tech: ['Android', 'Kotlin', 'WebRTC', 'Firebase', 'HealthKit', 'Node.js'],
    outcome: [
      { value: 'Live', label: 'Vitals during call' },
      { value: 'Open', label: 'Source available' }
    ],
    lesson:
      'Video quality degraded gracefully or the call was useless. Adaptive bitrate mattered more than resolution.',
    github: 'https://github.com/vdotok/Android-DoctorPatientApp',
    media: { images: ['vdotok.jpg', 'vdotoke.jpg'] }
  },
  {
    id: 'eve',
    title: 'Eve',
    category: 'Social',
    year: '2023',
    folder: 'eve_socal_app',
    cover: '/images/eve_socal_app.jpg',
    tags: ['React Native', 'Social', 'Real-time'],
    summary: 'A social app built around small communities instead of a global feed.',
    overview:
      'Eve is a social platform organised around interest-based spaces rather than one algorithmic timeline, with real-time posting and discovery scoped to the communities you join.',
    problem:
      'Large social feeds flatten everything into one stream, so smaller communities get drowned out and members stop posting.',
    solution:
      'Community spaces with their own feeds and moderation, plus discovery that surfaces active spaces rather than viral individual posts.',
    role:
      'Full-stack engineer. Built the React Native app, the real-time feed layer, and the moderation tooling.',
    features: [
      'Interest-based community spaces',
      'Real-time posting and reactions',
      'Space-level moderation tools',
      'Discovery tuned to activity, not virality',
      'Granular privacy controls'
    ],
    tech: ['React Native', 'Node.js', 'MongoDB', 'Socket.io', 'AWS'],
    outcome: [
      { value: 'Real-time', label: 'Feed updates' },
      { value: 'Per-space', label: 'Moderation' }
    ],
    lesson:
      'Moderation tools are a launch feature, not a later one. Communities need them from day one.',
    media: {
      images: ['Home (1).png', 'Post.png', 'Explore.png', 'Profile -- Business.png']
    }
  }
];

export const getProject = (id) => projects.find((project) => project.id === id);
